import { useState, type FormEvent } from 'react';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { useSeo } from '@/hooks/useSeo';

const FORM_ID = 'doll-sister-contact-form';
const FORM_ACTION = 'https://readdy.ai/api/form/db9j91k6oi3g3tv8v000';

const values = [
  {
    icon: 'ri-quill-pen-line',
    title: '先看懂，再推荐',
    text: '每一条推荐都由编辑完整看过资料与成片片段后撰写，不复制通稿，不堆砌形容词。',
  },
  {
    icon: 'ri-layout-grid-line',
    title: '分类要经得起检索',
    text: '类型、年份、地区、连载状态全部结构化，让你在十几秒内缩小到真正想看的那几部。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '不做播放与下载',
    text: '我们只提供信息与判断，不承载片源。收藏保存在你本机，不收集账号，也不需要登录。',
  },
];

const team = [
  { name: '林一舟', role: '主编 / 产业观察', tag: '负责票房与行业趋势' },
  { name: '苏木', role: '资深影评人', tag: '剧情片与作者电影' },
  { name: '顾清和', role: '剧集编辑', tag: '国产剧与日韩剧' },
  { name: '何砚之', role: '特约撰稿', tag: '类型片与悬疑专题' },
];

export default function AboutPage() {
  useSeo({
    title: '关于我们 - 玩偶姐姐影视内容平台',
    description:
      '了解玩偶姐姐影视内容平台的定位、内容标准与编辑团队，并通过联系表单与编辑部沟通合作、纠错与版权事宜。',
    keywords: '玩偶姐姐,关于我们,影视平台介绍,编辑团队,联系方式',
  });

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [successText, setSuccessText] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('company_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setSuccessText('留言已提交，我们会尽快回复你。');
      form.reset();
      return;
    }

    const name = String(formData.get('name') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const message = String(formData.get('message') ?? '').trim();

    if (!name || !email || !message) {
      setStatus('error');
      setFormError('请填写称呼、邮箱与留言内容后再提交。');
      return;
    }
    if (message.length > 500) {
      setStatus('error');
      setFormError('留言内容不能超过 500 个字符，请精简后再提交。');
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'company_alt') return;
      if (typeof value === 'string') payload.append(key, value);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ACTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '';
      const isSpam = String(serverMessage).toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK' && !isSpam;

      if (!isOk) {
        setStatus('error');
        setFormError(
          isSpam
            ? '提交被判定为异常请求，请稍后再试。'
            : serverMessage || '提交失败，请稍后重试，或直接发送邮件与我们联系。',
        );
        return;
      }

      setStatus('success');
      setSuccessText('留言已提交，我们会在两个工作日内回复你。');
      form.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，提交没有成功。请检查网络后重试，或直接发送邮件。');
    }
  };

  return (
    <div className="w-full">
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="w-full px-4 py-6 md:px-8 md:py-10">
          <Breadcrumbs items={[{ label: '关于' }]} />
          <div className="mt-5 grid grid-cols-1 gap-6 md:grid-cols-[1.4fr_1fr] md:items-end">
            <div>
              <p className="font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-600">
                About Doll Sister
              </p>
              <h1 className="mt-2.5 font-heading text-2xl font-bold leading-tight tracking-tight text-foreground-950 md:text-[34px]">
                一个认真写字、认真选片的影视内容平台
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
                「玩偶姐姐」创建于 2023 年，最初只是四个影迷共用的片单文档。三年过去，它变成一个有分类、有筛选、有编辑判断的内容站，但出发点没变：帮你在太多选择里，少花一点时间纠结。
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: '2023', label: '平台创建' },
                { value: '20', label: '收录作品' },
                { value: '8', label: '每日更新' },
              ].map((stat, index) => (
                <div
                  key={stat.label}
                  className={`rounded-md p-3.5 ${index === 1 ? 'bg-accent-500' : 'bg-background-50'}`}
                >
                  <p className="font-heading text-xl font-bold leading-none text-foreground-950">{stat.value}</p>
                  <p className="mt-1.5 font-label text-[11px] text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 py-10 md:px-8 md:py-14">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">我们坚持的三件事</h2>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {values.map((value, index) => (
            <div key={value.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span
                className={`flex h-10 w-10 items-center justify-center rounded-md ${
                  index === 1 ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-primary-600'
                }`}
              >
                <i className={`${value.icon} text-[19px]`}></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{value.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{value.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full bg-background-100 px-4 py-10 md:px-8 md:py-14">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-600">
              The Team
            </p>
            <h2 className="mt-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              编辑团队
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-600">
              四个人，四种口味。我们尽量让片单不被单一审美统治。
            </p>
          </div>
          <span className="font-label text-[11px] text-foreground-500">团队信息为示例内容，用于演示页面结构</span>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {team.map((member, index) => (
            <div key={member.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span
                className={`flex h-12 w-12 items-center justify-center rounded-full ${
                  index % 2 === 0 ? 'bg-primary-100 text-primary-700' : 'bg-accent-200 text-accent-900'
                }`}
              >
                <i className="ri-user-star-line text-[22px]"></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{member.name}</h3>
              <p className="mt-1 text-xs text-primary-700">{member.role}</p>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">{member.tag}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.15fr] lg:gap-10">
          <div>
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-600">
              Contact
            </p>
            <h2 className="mt-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              联系我们
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              内容合作、版权事宜、资料纠错，或只是想聊聊某部片子的结尾——都可以通过下面的表单发给我们。工作日内两个工作日回复。
            </p>

            <div className="mt-6 space-y-3">
              <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-primary-600">
                  <i className="ri-mail-line text-[16px]"></i>
                </span>
                <div>
                  <p className="font-label text-[11px] text-foreground-500">编辑部邮箱</p>
                  <p className="mt-0.5 text-sm font-medium text-foreground-900">hello@dollfilm.example</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-primary-600">
                  <i className="ri-map-pin-line text-[16px]"></i>
                </span>
                <div>
                  <p className="font-label text-[11px] text-foreground-500">办公地址</p>
                  <p className="mt-0.5 text-sm font-medium text-foreground-900">
                    北京市朝阳区影像中心 3 层
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-primary-600">
                  <i className="ri-time-line text-[16px]"></i>
                </span>
                <div>
                  <p className="font-label text-[11px] text-foreground-500">回复时间</p>
                  <p className="mt-0.5 text-sm font-medium text-foreground-900">周一至周五 10:00 - 19:00</p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
            <form id={FORM_ID} data-readdy-form onSubmit={handleSubmit} action={FORM_ACTION} method="post">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="block font-label text-xs text-foreground-700">
                    你的称呼 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block font-label text-xs text-foreground-700">
                    邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="name@example.com"
                    className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="contact-topic" className="block font-label text-xs text-foreground-700">
                  咨询类型
                </label>
                <select
                  id="contact-topic"
                  name="topic"
                  defaultValue="内容合作"
                  className="mt-1.5 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400"
                >
                  <option value="内容合作">内容合作</option>
                  <option value="版权事宜">版权事宜</option>
                  <option value="资料纠错">资料纠错</option>
                  <option value="观众反馈">观众反馈</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between">
                  <label htmlFor="contact-message" className="font-label text-xs text-foreground-700">
                    留言内容 <span className="text-primary-600">*</span>
                  </label>
                  <span className="font-label text-[10px] text-foreground-400">最多 500 字</span>
                </div>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="简单说明你的问题或需求，我们会尽快回复"
                  className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                ></textarea>
              </div>

              <div className="honeypot-field">
                <label htmlFor="contact-company-alt">公司名称</label>
                <input
                  id="contact-company-alt"
                  name="company_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {status === 'error' && formError && (
                <p className="mt-3 flex items-start gap-2 rounded-md bg-primary-50 px-3 py-2.5 text-xs leading-relaxed text-primary-800">
                  <i className="ri-error-warning-line mt-0.5 text-[14px]"></i>
                  {formError}
                </p>
              )}
              {status === 'success' && (
                <p className="mt-3 flex items-start gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-xs leading-relaxed text-accent-900">
                  <i className="ri-checkbox-circle-line mt-0.5 text-[14px]"></i>
                  {successText}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="mt-4 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-[16px]"></i>
                    正在提交
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line text-[15px]"></i>
                    提交留言
                  </>
                )}
              </button>
              <p className="mt-2.5 text-center font-label text-[10px] leading-relaxed text-foreground-400">
                提交即表示同意我们使用该邮箱回复你的咨询，不会用于其他用途。
              </p>
            </form>
          </div>
        </div>
      </section>

      <section className="w-full px-4 pb-12 md:px-8">
        <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
          <h2 className="font-heading text-sm font-semibold text-foreground-950">使用说明与版权声明</h2>
          <div className="mt-3 space-y-2 text-xs leading-relaxed text-foreground-600">
            <p>
              本站所有影视作品信息、评分与剧情简介均为示例内容，仅用于展示平台功能，不构成观看建议或商业推荐。本站不提供任何影片播放、下载或资源分发服务。
            </p>
            <p>
              站内文字内容由玩偶姐姐编辑部撰写，转载请联系我们并注明出处。页面中使用的图片为生成示意图，如涉及权利问题请通过上方表单联系我们，我们会及时处理。
            </p>
            <p>收藏功能仅将内容编号保存在你的浏览器本地，清除浏览器数据后收藏记录会一并删除。</p>
          </div>
        </div>
      </section>
    </div>
  );
}